import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { Input } from './ui/Input';
import AddDishModal from './AddDishModal';

export function Header({ user, onSearchChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDishModal, setShowAddDishModal] = useState(false);

  const navItems = [
    { id: 'feed', label: 'Feed', path: '/feed' },
    { id: 'location', label: 'Local', path: '/' },
    { id: 'supper', label: 'Supper Club', path: '/supperclub' },
    { id: 'leaderboard', label: 'Leaderboard', path: '/leaderboard' },
    { id: 'profile', label: 'Profile', path: '/profile' },
  ];

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/feed') return 'feed';
    if (path.includes('/best-of') || path === '/') return 'location';
    if (path === '/supperclub') return 'supper';
    if (path === '/leaderboard') return 'leaderboard';
    if (path === '/profile') return 'profile';
    return '';
  };

  const activeTab = getActiveTab();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const getInitials = (email) => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <header className="border-b border-border bg-card">
      <div className="container mx-auto px-8 py-6">
        {/* Top row */}
        <div className="flex items-center justify-between mb-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <h1 className="serif text-3xl tracking-tight">RatedBite</h1>
          </Link>

          {/* Search */}
          <div className="flex-1 max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10 pr-4 h-10 border-border bg-background text-sm"
            />
          </div>

          {/* User info */}
          <div className="flex items-center gap-6">
            {user ? (
              <button
                onClick={() => setShowAddDishModal(true)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Add Dish
              </button>
            ) : (
              <Link 
                to="/login" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Add Dish
              </Link>
            )}
            <div className="h-8 w-px bg-border"></div>
            {user ? (
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-xs">{getInitials(user.email)}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link 
                  to="/login" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Login
                </Link>
                <Link 
                  to="/signup" 
                  className="text-sm bg-foreground text-background px-4 py-2 rounded-md hover:bg-foreground/90 transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex items-center gap-8 border-t border-border pt-4">
          {navItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <Link
                key={item.id}
                to={item.path}
                className={`
                  text-sm tracking-wide transition-colors relative pb-1
                  ${isActive 
                    ? 'text-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                {item.label}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-foreground"></div>
                )}
              </Link>
            );
          })}
          
          <button className="ml-auto text-muted-foreground hover:text-foreground transition-colors">
            <Menu className="w-5 h-5" />
          </button>
        </nav>
      </div>

      {/* Add Dish Modal */}
      {showAddDishModal && (
        <AddDishModal
          onClose={() => setShowAddDishModal(false)}
          onAdd={() => {
            setShowAddDishModal(false);
            // Optionally refresh or navigate
          }}
        />
      )}
    </header>
  );
}

